import path from 'path';
import { RouteMatch, TabParams } from './types';
import { TabConfig, TabMatch } from './types';

// Utility function to match routes
const matchRoute = (pattern: string, path: string): RouteMatch | null => {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);

  // Handle wildcard patterns (e.g., "/datasets/*")
  if (pattern.includes('*')) {
    const starIndex = patternParts.indexOf('*');
    if (pathParts.length < starIndex) {
      return null;
    }

    const matchingParts = patternParts.slice(0, starIndex);
    const pathPrefix = pathParts.slice(0, starIndex);

    if (
      matchingParts.every(
        (part, i) => part === pathPrefix[i] || part.startsWith(':')
      )
    ) {
      const params: Omit<TabParams, 'tabId'> = {};
      matchingParts.forEach((part, i) => {
        if (part.startsWith(':')) {
          params[part.slice(1)] = pathPrefix[i]!;
        }
      });
      return {
        params,
        remainingPath: '/' + pathParts.slice(starIndex).join('/'),
      };
    }
    return null;
  }

  // Handle exact matches and parameter patterns
  if (patternParts.length !== pathParts.length) {
    return null;
  }

  const params: Omit<TabParams, 'tabId'> = {};
  const matches = patternParts.every((part, i) => {
    if (part.startsWith(':')) {
      params[part.slice(1)] = pathParts[i]!;
      return true;
    }
    return part === pathParts[i];
  });

  return matches ? { params, remainingPath: '' } : null;
};

// Find matching tab configuration
export const findMatchingTab = (
  tabs: Omit<TabConfig, 'children'>[],
  path: string
): TabMatch | null => {
  // Strip hash from path before matching
  const pathWithoutHash = path.split('#')[0] ?? path;

  for (const tab of tabs) {
    const match = matchRoute(tab.path, pathWithoutHash);
    if (match && match.remainingPath === '') {
      // Exact match found
      return {
        element: tab.element,
        params: match.params,
        path: tab.path,
      };
    }
  }
  return null;
};

export const flattenConfigs = (configs: TabConfig[]): TabConfig[] => {
  return configs.flatMap((config) => {
    const children = config.children || [];
    const flattenedChildren = children.map((child) => ({
      ...child,
      path: path.join(config.path, child.path).replace(/\\/g, '/'),
    }));
    return [config, ...flattenConfigs(flattenedChildren)];
  });
};
